import { Children, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { getSpacingToken } from '@optimus-web/common';
import { Virtualizer } from '@optimus-web/helpers';
import { SelectListContainer } from './style';
import { EmptyState } from './SelectListEmptyState';

interface SelectListProps {
    selectedIndex?: number;
    virtualized?: boolean;
    defaultItemHeight?: number;
    maxHeight: number | 'fit-content';
    children?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    keyboardHandled?: boolean;
    searchContainerHeight: number;
    childrenCount?: number;
    selectAll?: boolean;
    hasFooter?: boolean;
}

export interface ISelectList {
    updateMaxHeight: (maxHeight: number) => void;
    containerRef: HTMLDivElement | null;
    focus: () => void;
}

export const DEFAULT_DESKTOP_ITEM_HEIGHT = 40;
export const DEFAULT_MOBILE_ITEM_HEIGHT = 56;
export const FOOTER_HEIGHT = 57; // 56px + 1px for the divider

export const SelectList = forwardRef(
    (
        {
            virtualized,
            maxHeight,
            children,
            emptyMessage,
            childrenCount: childrenCountFromProps,
            defaultItemHeight = DEFAULT_DESKTOP_ITEM_HEIGHT,
            selectedIndex,
            keyboardHandled,
            searchContainerHeight,
            hasFooter = false,
            ...other
        }: SelectListProps,
        ref
    ) => {
        const [overlayMaxHeight, setOverlayMaxHeight] = useState<number>(Number.POSITIVE_INFINITY);
        const [childrenHeight, setChildrenHeight] = useState<number>(Number.POSITIVE_INFINITY);
        const containerRef = useRef<HTMLDivElement>(null);
        const childrenArray = useMemo(() => Children.toArray(children), [children]);
        const childrenCount = childrenCountFromProps ?? childrenArray.length;
        const theme = useTheme();

        // Calculate total padding for virtualized list
        const totalPadding = useMemo(() => {
            if (virtualized) {
                // top + bottom padding + items margin
                return getSpacingToken('200')({ theme }) + childrenCount * getSpacingToken('50')({ theme });
            }
            return 0;
        }, [childrenCount, theme, virtualized]);

        // Focus method for keyboard navigation
        const focusElement = useCallback(() => {
            if (containerRef.current) {
                containerRef.current.focus();
            }
        }, []);

        // Expose methods through ref
        useImperativeHandle(
            ref,
            () => ({
                updateMaxHeight: (height: number) => {
                    setOverlayMaxHeight(height);
                },
                get containerRef() {
                    return containerRef.current;
                },
                focus: focusElement,
            }),
            [focusElement]
        );

        // Handle size changes from Virtualizer
        const handleTotalSizeChange = useCallback(
            (size: number) => {
                setChildrenHeight(size + totalPadding);
            },
            [totalPadding]
        );

        if (childrenCount === 0) {
            return <EmptyState emptyMessage={emptyMessage} />;
        }

        const containerHeight = calculateContainerHeight({
            maxHeight,
            overlayMaxHeight,
            childrenHeight,
            searchContainerHeight,
            hasFooter,
            virtualized,
        });

        return (
            <SelectListContainer ref={containerRef} virtualized={virtualized} {...other} role="listbox">
                {virtualized ? (
                    <Virtualizer
                        items={childrenArray}
                        estimatedSize={defaultItemHeight}
                        height={containerHeight}
                        scrollToIndex={keyboardHandled ? selectedIndex : undefined}
                        overscan={3}
                        onTotalSizeChange={handleTotalSizeChange}
                        preventResizeObserverLoop
                    />
                ) : (
                    children
                )}
            </SelectListContainer>
        );
    }
);

type CalculateContainerHeightParams = Pick<
    SelectListProps,
    'maxHeight' | 'searchContainerHeight' | 'hasFooter' | 'virtualized'
> & {
    overlayMaxHeight: number;
    childrenHeight: number;
};

const calculateContainerHeight = ({
    maxHeight,
    searchContainerHeight,
    hasFooter,
    virtualized,
    overlayMaxHeight,
    childrenHeight,
}: CalculateContainerHeightParams): number | 'fit-content' => {
    if (typeof maxHeight !== 'number') {
        return 'fit-content';
    }

    let adjustedOverlayMaxHeight = overlayMaxHeight;
    adjustedOverlayMaxHeight -= searchContainerHeight;

    if (hasFooter && virtualized) {
        adjustedOverlayMaxHeight -= FOOTER_HEIGHT;
    }

    return Math.min(maxHeight, adjustedOverlayMaxHeight, childrenHeight);
};
